import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Course, QuizListType } from "utils/interfaces";
import { getCollectionRequest } from "services/apiRequests";
import { usePlatform } from "components/utils/PlatformContext";
import { AnimatePresence, motion } from "framer-motion";
import MainHeader from "components/main-view/utils/main-header";
import ActivityTimer from "components/activity-timer/ActivityTimer";
import TakeQuiz from "components/quizzes/TakeQuiz";
import { logoutFunction, validateClient } from "services/authRequests";
import { RootContext } from "components/utils/Contexts";
import { getUserIdFromToken } from "services/userRequests";

export interface QuizProgress {
  quizId: string;
  currentQuestionIndex: number;
  correctAnswers: Record<any, any>;
  answers: Record<number, any>; // Store answers by question index
  timestamp: number;
}

const Quiz: React.FC = () => {
  const { quizId } = useParams();

  // States definition
  const [userRole, setUserRole] = useState("student");
  const [data, setData] = useState<QuizListType | undefined>(undefined);
  const [savedProgress, setSavedProgress] = useState<QuizProgress | null>(null);

  // User information
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  // Get the platform context
  const { quizWatermark1, quizWatermark2 } = usePlatform();

  // Get context values
  const { setFeedbucketUsername, setFeedbucketEmail, setFeedbucketToken } =
    useContext(RootContext);

  // refs
  const headerRef: any = useRef();

  // Define a navigator state
  const navigate = useNavigate();
  const { state } = useLocation();

  const _courseidx =
    state && state.navRegister
      ? state.navRegister.findIndex(
          (elem: any) => elem.lessonListToShow === "courses"
        )
      : -1;
  let course: Course | undefined = undefined;
  if (_courseidx > 0) course = state.navRegister[_courseidx].record;

  /**
   * Load saved progress from localStorage
   */
  const loadSavedProgress = (): QuizProgress | null => {
    if (!quizId) return null;

    const userId = getUserIdFromToken();
    const storageKey = `quiz_progress_${userId}_${quizId}`;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const progress: QuizProgress = JSON.parse(saved);

        // Check if progress is less than 7 days old
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - progress.timestamp < sevenDaysInMs) {
          return progress;
        } else {
          // Clear old progress
          localStorage.removeItem(storageKey);
        }
      }
    } catch (error) {
      console.error("Error loading quiz progress:", error);
    }

    return null;
  };

  /**
   * Save progress to localStorage
   */
  const saveProgress = (progress: Omit<QuizProgress, "timestamp">) => {
    if (!quizId) return;

    const userId = getUserIdFromToken();
    const storageKey = `quiz_progress_${userId}_${quizId}`;

    try {
      const progressWithTimestamp: QuizProgress = {
        ...progress,
        timestamp: Date.now(),
      };
      localStorage.setItem(storageKey, JSON.stringify(progressWithTimestamp));
    } catch (error) {
      console.error("Error saving quiz progress:", error);
    }
  };

  /**
   * Clear saved progress
   */
  const clearProgress = () => {
    if (!quizId) return;

    const userId = getUserIdFromToken();
    const storageKey = `quiz_progress_${userId}_${quizId}`;

    try {
      localStorage.removeItem(storageKey);
      setSavedProgress(null);
    } catch (error) {
      console.error("Error clearing quiz progress:", error);
    }
  };

  /**
   * Check if the user has an access token every time we enter this view
   */
  useEffect(() => {
    validateClient().then(
      (response: { connected: boolean; navigateTo: string }) => {
        // Check if the token is validated
        if (!response.connected) {
          return logoutFunction();
        } else {
          // Get the user's profile info
          getCollectionRequest("/api/users/my-profile-info").then(
            (response) => {
              // Check if the response is valid
              if (!(response && response.successful)) {
                return logoutFunction();
              }

              // Get the user
              const user = response.content;

              // And set the states
              setUserFirstName(user.firstName);
              setUserLastName(user.lastName);
              setUserRole(user.role);
              setUserEmail(user.email);
            }
          );
        }
      }
    );
  }, []);

  /**
   * Load saved progress on mount
   */
  useEffect(() => {
    const progress = loadSavedProgress();
    if (progress) {
      setSavedProgress(progress);
    }
  }, [quizId]);

  /**
   * Assign the role based on the specified in the login view
   */
  useEffect(() => {
    getData();
    if (course) {
      headerRef.current.setHeaderTitle(course.title);
    } else {
      headerRef.current.setHeaderTitle("Quizzes");
    }
    headerRef.current.setShowBackButton(true);
  }, []);

  /**
   * Function to get the data depending on the class
   * @returns data
   */
  const getData = async () => {
    const response = await getCollectionRequest(
      `/api/quizzes/get-quiz?quizId=${quizId}`
    );

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return undefined;
    }

    // Get the data
    const data = response.content;

    if (data.length === 1) {
      setData(data[0]);
      return data[0];
    } else {
      console.error("malformed data quiz:", data);
      goToDashboard();
    }
  };

  // Return to home url
  const goToDashboard = () => {
    // return
    if (state) {
      navigate("/home", { state });
    } else {
      navigate("/home", {
        state: {
          activeItem: userRole === "master admin" ? "quizzes" : "lessons",
        },
      });
    }
  };

  /**
   * Define the subtitle once the data is read
   */
  useEffect(() => {
    if (headerRef.current && data !== undefined) {
      headerRef.current.setHeaderDescription(
        `${data.questions ? data.questions.length : 0} question${
          (data.questions ? data.questions.length : 0) === 1 ? "" : "s"
        }`
      );
    }
  }, [data]);

  /**
   * Define the feedbucket profile
   */
  useEffect(() => {
    if (userFirstName && userLastName) {
      setFeedbucketUsername(`${userFirstName} ${userLastName}`);
      setFeedbucketEmail(userEmail);
      setFeedbucketToken(getUserIdFromToken());
    }
  }, [userFirstName, userLastName, userEmail]);

  return (
    <div className="w-screen h-screen p-8 flex flex-col gap-4">
      <ActivityTimer type="quizzes" itemId={quizId} intervalMs={30000} />
      <MainHeader ref={headerRef} backButtonFunction={() => goToDashboard()} />
      <AnimatePresence>
        {data !== undefined && (
          <>
            {quizWatermark1 && (
              <motion.img
                key="watermark-1"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 0.2,
                }}
                exit={{
                  opacity: 0,
                }}
                alt="watermark1"
                className="fixed w-[33vw] max-w-[360px] right-4 top-4 select-none"
                src={quizWatermark1}
              />
            )}
            {quizWatermark2 && (
              <motion.img
                key="watermark-2"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 0.2,
                }}
                exit={{
                  opacity: 0,
                }}
                alt="watermark2"
                className="fixed w-[33vw] max-w-[360px] left-0 bottom-4 select-none"
                src={quizWatermark2}
              />
            )}
          </>
        )}
      </AnimatePresence>
      {data !== undefined && (
        <TakeQuiz
          quiz={data}
          exitQuiz={goToDashboard}
          savedProgress={savedProgress}
          onProgressUpdate={saveProgress}
          onComplete={clearProgress}
        />
      )}
    </div>
  );
};

export default Quiz;
